import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { mayaCustomerInitiativeListRoute } from 'basics/constants/routes.constants';
import { useUpdateCustomerInitiativeMutation } from 'basics/graphql/mutations/updateCustomerInitiative';
import useGetCustomerInitiativeByIdQuery from 'basics/graphql/queries/getCustomerInitiativeById';
import { succesToast, warningToast } from 'basics/utils/toast';
import {
  CiFormCanceltCallbackType,
  CiFormSubmitCallbackType,
} from 'components/CustomerInitiativeForm/CustomerInitiativeForm.types';

const useCustomerInitiativeEdit = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [updateCustomerInitiative] = useUpdateCustomerInitiativeMutation();

  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const location = useLocation();
  const currentURL = location.pathname;

  const [loading, setLoading] = useState(true);

  const customerInitiative = useGetCustomerInitiativeByIdQuery(urlParams.get('id')).data;

  useEffect(() => {
    if (customerInitiative) {
      setLoading(false);
    }
  }, [customerInitiative, setLoading]);

  const handleCancelCustomerInitiativeForm: CiFormCanceltCallbackType = () => {
    navigate(`${mayaCustomerInitiativeListRoute}?year=${customerInitiative?.year}`);
  };

  const handleSaveCustomerInitiativeForm: CiFormSubmitCallbackType = async (ci) => {
    const { data: customerInitiativeData, error } = await updateCustomerInitiative(
      customerInitiative?._id || null,
      { ...ci, isTemplate: false },
    );

    const year = new Date(ci.dateStart).getFullYear();

    if (customerInitiativeData && !error) {
      succesToast(t('maya_ci_updated', { title: ci.title }));
      navigate(`${mayaCustomerInitiativeListRoute}?year=${year}`);
    } else {
      warningToast(error || t('maya_ci_updated_error', { title: ci.title }));
    }
  };

  return {
    loading,
    customerInitiative,
    currentURL,
    handleSaveCustomerInitiativeForm,
    handleCancelCustomerInitiativeForm,
  };
};

export default useCustomerInitiativeEdit;
